import type {
  TGuaranteesMoneyValue,
  TGuaranteesTenderDetails,
  TGuaranteesTenderSearchResponse,
  TGuaranteesTenderValue,
} from './types.ts';

const normalizeString = (value: unknown): string => {
  if (typeof value === 'string') {
    return value.trim();
  }

  if (typeof value === 'number') {
    return String(value);
  }

  return '';
};

const normalizeNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim()) {
    const parsedValue = Number(value.replace(/\s/g, ''));

    if (Number.isFinite(parsedValue)) {
      return parsedValue;
    }
  }

  return null;
};

const normalizeBoolean = (value: unknown): boolean | null => {
  if (typeof value === 'boolean') {
    return value;
  }

  return null;
};

export const mapGuaranteesMoneyValue = (
  value?: TGuaranteesTenderValue | null
): TGuaranteesMoneyValue => ({
  amount: normalizeNumber(value?.amount),
  currency: normalizeString(value?.currency),
  valueAddedTaxIncluded: normalizeBoolean(value?.valueAddedTaxIncluded),
});

const getGuaranteesTenderExpiryDate = (
  value?: {
    guarantee?: {
      validityPeriod?: { endDate?: string | null } | null;
      expiryDate?: string | null;
      expiry_date?: string | null;
    } | null;
    guaranteeExpiryDate?: string | null;
    guarantee_expiry_date?: string | null;
    tenderPeriod?: { endDate?: string | null } | null;
  } | null
): string =>
  normalizeString(value?.guarantee?.validityPeriod?.endDate) ||
  normalizeString(value?.guarantee?.expiryDate) ||
  normalizeString(value?.guarantee?.expiry_date) ||
  normalizeString(value?.guaranteeExpiryDate) ||
  normalizeString(value?.guarantee_expiry_date) ||
  normalizeString(value?.tenderPeriod?.endDate);

export const getGuaranteesTenderSelectedExpiryDate = (
  tenderDetails: TGuaranteesTenderDetails | null,
  selectedLotId: string
): string => {
  const selectedLot = tenderDetails?.lots.find((lot) => lot.id === selectedLotId);

  return selectedLot?.guaranteeExpiryDate || tenderDetails?.summary.guaranteeExpiryDate || '';
};

export const buildAddressString = (response: TGuaranteesTenderSearchResponse): string =>
  [
    response.data.procuringEntity?.address?.countryName,
    response.data.procuringEntity?.address?.region,
    response.data.procuringEntity?.address?.locality,
    response.data.procuringEntity?.address?.streetAddress,
    response.data.procuringEntity?.address?.postalCode,
  ]
    .map(normalizeString)
    .filter(Boolean)
    .join(', ');

export const mapGuaranteesTenderResponseToDetails = (
  response: TGuaranteesTenderSearchResponse
): TGuaranteesTenderDetails => {
  const expectedValue = mapGuaranteesMoneyValue(response.data.value);
  const minimalStep = mapGuaranteesMoneyValue(response.data.minimalStep);
  const items = response.data.items ?? [];
  const lots = response.data.lots ?? [];

  return {
    summary: {
      id: normalizeString(response.data.id),
      tenderId: normalizeString(response.data.tenderID),
      title: normalizeString(response.data.title),
      description: normalizeString(response.data.description),
      status: normalizeString(response.data.status),
      createdAt: normalizeString(response.data.dateCreated),
      updatedAt: normalizeString(response.data.dateModified),
      expectedValue,
      minimalStep,
      procurementMethod: normalizeString(response.data.procurementMethod),
      procurementMethodType: normalizeString(response.data.procurementMethodType),
      awardCriteria: normalizeString(response.data.awardCriteria),
      owner: normalizeString(response.data.owner),
      customerName: normalizeString(response.data.procuringEntity?.name),
      customerCode: normalizeString(response.data.procuringEntity?.identifier?.id),
      customerLegalName: normalizeString(response.data.procuringEntity?.identifier?.legalName),
      customerAddress: buildAddressString(response),
      contactName: normalizeString(response.data.procuringEntity?.contactPoint?.name),
      contactEmail: normalizeString(response.data.procuringEntity?.contactPoint?.email),
      contactTelephone: normalizeString(response.data.procuringEntity?.contactPoint?.telephone),
      contactUrl: normalizeString(response.data.procuringEntity?.contactPoint?.url),
      tenderPeriodStart: normalizeString(response.data.tenderPeriod?.startDate),
      tenderPeriodEnd: normalizeString(response.data.tenderPeriod?.endDate),
      enquiryPeriodStart: normalizeString(response.data.enquiryPeriod?.startDate),
      enquiryPeriodEnd: normalizeString(response.data.enquiryPeriod?.endDate),
      guaranteeExpiryDate: getGuaranteesTenderExpiryDate(response.data),
    },
    lots: lots.length
      ? lots.map((lot, index) => ({
          id:
            normalizeString(lot.id) ||
            `${normalizeString(response.data.tenderID) || 'tender'}-lot-${index + 1}`,
          title: normalizeString(lot.title) || normalizeString(lot.description),
          quantity: null,
          unitName: '',
          unitCode: '',
          classificationId: '',
          classificationDescription: '',
          classificationScheme: '',
          expectedValue: mapGuaranteesMoneyValue(lot.value),
          status: normalizeString(lot.status),
          guaranteeExpiryDate: getGuaranteesTenderExpiryDate(lot),
        }))
      : items.map((item, index) => ({
          id: `${normalizeString(response.data.tenderID) || 'tender'}-item-${index + 1}`,
          title: normalizeString(item.description),
          quantity: item.quantity ?? null,
          unitName: normalizeString(item.unit?.name),
          unitCode: normalizeString(item.unit?.code),
          classificationId: normalizeString(item.classification?.id),
          classificationDescription: normalizeString(item.classification?.description),
          classificationScheme: normalizeString(item.classification?.scheme),
          expectedValue: null,
          status: '',
          guaranteeExpiryDate: '',
        })),
    config: response.config ?? null,
  };
};
